import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    TextField,
} from "@mui/material";
import {CloseOutlined, ReplayOutlined} from "@mui/icons-material";
import type {ClContract, ClContractType} from "../contractLinkageData";
import type {ClContractPatch} from "../contractLinkageUtils";

type Props = {
    open: boolean;
    /** The contract as currently shown (extracted values + earlier edits). */
    contract: ClContract | null;
    /** The contract as originally extracted, to diff edits against. */
    base: ClContract | null;
    /** Called with the fields that differ from the extracted values; null clears all edits. */
    onSave: (patch: ClContractPatch | null) => void;
    onClose: () => void;
};

const CONTRACT_TYPES: ClContractType[] = [
    'Framework agreement', 'License agreement', 'Service contract', 'Project SOW', 'Maintenance contract',
];

const RENEWAL_TYPES = ['Auto-renewal', 'Manual renewal', 'Fixed term'];

type FieldSpec = {
    key: keyof ClContractPatch;
    label: string;
    kind?: 'text' | 'date' | 'number' | 'multiline';
    options?: string[];
    span2?: boolean;
};

const GROUPS: {title: string; fields: FieldSpec[]}[] = [
    {
        title: 'Contract',
        fields: [
            {key: 'name', label: 'Contract name', span2: true},
            {key: 'type', label: 'Contract type', options: CONTRACT_TYPES},
            {key: 'counterpartyEntity', label: 'Counterparty entity'},
            {key: 'contractOwner', label: 'Contract owner'},
            {key: 'ownerDepartment', label: 'Owner department'},
            {key: 'signedDate', label: 'Signed', kind: 'date'},
            {key: 'startDate', label: 'Effective', kind: 'date'},
            {key: 'endDate', label: 'Expires', kind: 'date'},
        ],
    },
    {
        title: 'Commercial terms',
        fields: [
            {key: 'contractedValue', label: 'Contracted value (EUR)', kind: 'number'},
            {key: 'paymentTerms', label: 'Payment terms'},
            {key: 'pricingModel', label: 'Pricing model', span2: true},
            {key: 'priceAdjustment', label: 'Price adjustment', span2: true},
        ],
    },
    {
        title: 'Renewal & termination',
        fields: [
            {key: 'renewalType', label: 'Renewal', options: RENEWAL_TYPES},
            {key: 'renewalNotice', label: 'Renewal notice'},
            {key: 'terminationForConvenience', label: 'Termination for convenience', span2: true},
        ],
    },
    {
        title: 'Legal, service & scope',
        fields: [
            {key: 'governingLaw', label: 'Governing law'},
            {key: 'liabilityCap', label: 'Liability cap'},
            {key: 'slaSummary', label: 'Service levels', kind: 'multiline', span2: true},
            {key: 'scopeSummary', label: 'Scope summary', kind: 'multiline', span2: true},
        ],
    },
];

const ALL_FIELDS = GROUPS.flatMap(g => g.fields);

const toForm = (c: ClContract): Record<string, string> => {
    const form: Record<string, string> = {};
    ALL_FIELDS.forEach(f => {
        const v = c[f.key as keyof ClContract];
        form[f.key] = v === null || v === undefined ? '' : String(v);
    });
    return form;
};

export const ContractEditDialog: React.FC<Props> = ({open, contract, base, onSave, onClose}) => {
    const [form, setForm] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
        if (open && contract) setForm(toForm(contract));
    }, [open, contract]);

    if (!contract || !base) return null;

    const baseForm = toForm(base);
    const hasEdits = ALL_FIELDS.some(f => form[f.key] !== undefined && form[f.key] !== baseForm[f.key]);

    const save = () => {
        const patch: ClContractPatch = {};
        ALL_FIELDS.forEach(f => {
            const value = form[f.key];
            if (value === undefined || value === baseForm[f.key]) return;
            if (f.kind === 'number') {
                const n = Number(value);
                if (Number.isFinite(n)) (patch as Record<string, unknown>)[f.key] = n;
            } else {
                (patch as Record<string, unknown>)[f.key] = value;
            }
        });
        onSave(Object.keys(patch).length > 0 ? patch : null);
        onClose();
    };

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
                sx={theme => ({zIndex: theme.zIndex.drawer + 3})}>
            <DialogTitle className="cl-picker-title">
                Edit contract details
                <IconButton size="small" onClick={onClose}><CloseOutlined/></IconButton>
            </DialogTitle>
            <DialogContent className="cl-editdlg-content">
                <p className="cl-editdlg-hint">
                    Correct or complete the details the AI extracted from <b>{base.sourceFile ?? 'the contract document'}</b>.
                    Your changes override the extracted values and are kept on this device for the showcase.
                </p>
                {GROUPS.map(group => (
                    <div key={group.title} className="cl-editdlg-group">
                        <span className="cl-meta-group-title">{group.title}</span>
                        <div className="cl-editdlg-grid">
                            {group.fields.map(f => {
                                const value = form[f.key] ?? '';
                                const edited = value !== baseForm[f.key];
                                const options = f.options && !f.options.includes(value) && value !== ''
                                    ? [...f.options, value]
                                    : f.options;
                                return (
                                    <TextField
                                        key={f.key}
                                        size="small"
                                        className={f.span2 ? 'cl-editdlg-span2' : undefined}
                                        label={edited ? `${f.label} (edited)` : f.label}
                                        select={!!options}
                                        type={f.kind === 'date' ? 'date' : f.kind === 'number' ? 'number' : 'text'}
                                        multiline={f.kind === 'multiline'}
                                        minRows={f.kind === 'multiline' ? 2 : undefined}
                                        value={value}
                                        onChange={e => setForm(prev => ({...prev, [f.key]: e.target.value}))}
                                        slotProps={{inputLabel: {shrink: true}}}>
                                        {options?.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                                    </TextField>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </DialogContent>
            <DialogActions className="cl-editdlg-actions">
                <Button
                    size="small"
                    startIcon={<ReplayOutlined/>}
                    disabled={!hasEdits}
                    onClick={() => setForm(toForm(base))}>
                    Reset to extracted values
                </Button>
                <span style={{flex: 1}}/>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" onClick={save}>Save changes</Button>
            </DialogActions>
        </Dialog>
    );
};
